// Contact.jsx - Main component that coordinates the contact information system
import React, { useState, useEffect, useRef } from 'react';
import { Container, Alert, Button } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { useAuth } from '../../../AuthContext/AuthContext';

// Import child components
import ContactCard from './ContactCard';
import ContactEditor from './ContactFormEditor';

// Import actions and utilities
import { fetchContactData, saveContactData, deleteContactData } from './ContactApiActions';
import { validateContactForm, initializeContactForm } from './ContactFormUtils';
import { sanitizeForm } from '../../../Utilities/DataCleanUp';
import {useTranslation} from 'react-i18next'

/**
 * Main Contact component that manages the display and editing of contact information.
 * Handles data fetching, state management, and coordination between child components.
 *
 * @param {Object} props
 * @param {Function} props.onSave - Callback when contact data is saved
 * @param {Function} props.onValidationChange - Callback for validation status changes
 * @param {Object} props.data - Initial data (if any)
 */
const Contact = ({ onSave, onValidationChange, data }) => {

    const {t} = useTranslation('contact')
    // Core state management
    const { currentUser } = useAuth();
    const [contactData, setContactData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Prevent duplicate fetches
    const fetchedRef = useRef(false);

    // Load initial data when component mounts
    useEffect(() => {
        const loadData = async () => {
            if (!currentUser?.id || fetchedRef.current) return;

            try {
                setLoading(true);
                const data = await fetchContactData();

                if (data) {
                    setContactData(data);
                    validateAndUpdateProgress(data);
                }

                fetchedRef.current = true;
            } catch (error) {
                console.error('Error loading contact data:', error);
                setError(t('errorLoad'));
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [currentUser?.id, t]);

    /**
     * Validates form data and updates progress tracking
     * @param {Object} data - The contact data to validate
     * @returns {boolean} Whether the data is valid
     */
    const validateAndUpdateProgress = (data) => {
        const errors = validateContactForm(data);
        const isValid = Object.keys(errors).length === 0;

        setFormErrors(errors);

        if (typeof onValidationChange === 'function') {
            onValidationChange(isValid);
        }

        return isValid;
    };

    /**
     * Handles adding new contact information
     */
    const handleAdd = () => {
        setContactData(initializeContactForm());
        setIsEditing(true);
        setError(null);
    };

    /**
     * Handles saving contact information
     * @param {Object} updatedData - The new contact data to save
     */
    const handleSave = async (updatedData) => {
        try {
            setLoading(true);
            setError(null);

            const sanitizedData = sanitizeForm({
                ...updatedData,
                user_id: currentUser.id,
            });

            if (!validateAndUpdateProgress(sanitizedData)) {
                return;
            }

            const savedData = await saveContactData(sanitizedData, currentUser);

            setContactData(savedData);
            setIsEditing(false);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);

            onSave && onSave(savedData);
            setFormErrors({});
        } catch (error) {
            console.error('Save error:', error);
            setError(t('errorSave'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handles deleting contact information
     */
    const handleDelete = async () => {
        try {
            setLoading(true);
            setError(null);

            if (contactData?.id) {
                await deleteContactData(contactData.id);
                setContactData(null);
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
            }
        } catch (error) {
            console.error('Delete error:', error);
            setError(t('errorDelete'));
        } finally {
            setLoading(false);
        }
    };

    // Render component
    return (
        <Container className="mt-4 mb-5">
            {/* Header */}
            <h2 className="text-center mb-4" style={{ fontWeight: 'bold', color: '#4682B4' }}>
                {t('contactInformation')}
            </h2>

            {/* Status indicators */}
            {loading && <Alert variant="info">{t('loading')}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            {success && (
                <Alert variant="success">
                    {isEditing ? t('successSave') : t('successEdit')}
                </Alert>
            )}

            {/* Empty state */}
            {!loading && !contactData && !isEditing && (
                <>
                    <Alert variant="info" className="text-center">
                        {t('noContactInfo')}
                    </Alert>
                    <div className="d-flex justify-content-center">
                        <Button variant="success" onClick={handleAdd}>
                            <FaPlus className="me-2" />
                            {t('addContactInfo')}
                        </Button>
                    </div>
                </>
            )}

            {/* Display mode */}
            {!loading && contactData && !isEditing && (
                <ContactCard
                    contactData={contactData}
                    currentUser={currentUser}
                    onEdit={() => setIsEditing(true)}
                    onDelete={handleDelete}
                />
            )}

            {/* Edit mode */}
            {isEditing && (
                <ContactEditor
                    formData={contactData}
                    currentUser={currentUser}
                    onSave={handleSave}
                    onCancel={() => {
                        setIsEditing(false);
                        setError(null);
                    }}
                    formErrors={formErrors}
                />
            )}
        </Container>
    );
};

export default Contact;